import HomeIntro from "@/components/feature/HomeIntro";
import { useEffect, useMemo, useState } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import FeaturesBar from "@/components/feature/FeaturesBar";
import CartDrawer from "@/components/feature/CartDrawer";
import CategoryHero from "@/pages/home/components/CategoryHero";
import SidebarFilters from "@/pages/home/components/SidebarFilters";
import ProductListing from "@/pages/home/components/ProductListing";
import { categories as mockCategories, colors as mockColors, materials as mockMaterials, products as mockProducts, type Product } from "@/mocks/products";
import { useCart } from "@/context/CartContext";
import {
  fetchNuvemshopCategories,
  fetchNuvemshopProducts,
  isNuvemshopEnabled,
  type NuvemshopCategory,
} from "@/services/nuvemshop";
import type { FilterOption } from "@/pages/home/components/SidebarFilters";

const PAGE_SIZE = 12;
const ALL_CATEGORY_ID = "todos";

export default function Home() {
  const { addItem, openCart } = useCart();

  const [products, setProducts] = useState<Product[]>(isNuvemshopEnabled() ? [] : mockProducts);
  const [categories, setCategories] = useState<FilterOption[]>(
    isNuvemshopEnabled()
      ? [{ id: ALL_CATEGORY_ID, label: "Todos os produtos" }]
      : mockCategories.map((c) => ({ id: c.id, label: c.label }))
  );

  const [catalogError, setCatalogError] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(isNuvemshopEnabled());
  useEffect(() => {
    if (!isNuvemshopEnabled()) return;
    Promise.all([fetchNuvemshopProducts(), fetchNuvemshopCategories()])
      .then(([realProducts, realCategories]: [Product[], NuvemshopCategory[]]) => {
        setProducts(realProducts);
        setCategories([{ id: ALL_CATEGORY_ID, label: "Todos os produtos" }, ...realCategories]);
      })
      .catch(() => setCatalogError("Não foi possível carregar os produtos. Tente recarregar a página ou fale conosco."))
      .finally(() => setLoadingCatalog(false));
  }, []);

  const colorOptions: FilterOption[] = useMemo(() => {
    if (!isNuvemshopEnabled()) return mockColors.map((c) => ({ id: c.id, label: c.label }));
    const values = new Set(products.map((p) => p.color).filter(Boolean));
    return Array.from(values).map((value) => ({ id: value, label: value }));
  }, [products]);

  const materialOptions: FilterOption[] = useMemo(() => {
    if (!isNuvemshopEnabled()) return mockMaterials.map((m) => ({ id: m.id, label: m.label }));
    const values = new Set(products.map((p) => p.material).filter(Boolean));
    return Array.from(values).map((value) => ({ id: value, label: value }));
  }, [products]);

  const [selectedCategory, setSelectedCategory] = useState(isNuvemshopEnabled() ? ALL_CATEGORY_ID : "colares");
  const [selectedPrices, setSelectedPrices] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [sortId, setSortId] = useState("vendidos");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  const toggleValue = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setCurrentPage(1);
  };

  const activeCategory = categories.find((category) => category.id === selectedCategory);
  const title = activeCategory ? activeCategory.label.toUpperCase() : "PRODUTOS";

  const filtered = useMemo(() => {
    const categoryOk = (product: Product) => {
      if (!isNuvemshopEnabled()) return selectedCategory === "todos" || selectedCategory === "colares";
      if (selectedCategory === ALL_CATEGORY_ID) return true;
      return (product.categoryIds ?? []).includes(Number(selectedCategory));
    };

    const priceOk = (product: Product) => {
      if (selectedPrices.length === 0) return true;
      return selectedPrices.some((id) => {
        if (id === "p1") return product.price <= 50;
        if (id === "p2") return product.price > 50 && product.price <= 100;
        if (id === "p3") return product.price > 100 && product.price <= 200;
        if (id === "p4") return product.price > 200 && product.price <= 300;
        if (id === "p5") return product.price > 300;
        return true;
      });
    };

    const colorOk = (product: Product) =>
      selectedColors.length === 0 || selectedColors.includes(product.color);

    const materialOk = (product: Product) =>
      selectedMaterials.length === 0 || selectedMaterials.includes(product.material);

    return products.filter(
      (product) => categoryOk(product) && priceOk(product) && colorOk(product) && materialOk(product)
    );
  }, [products, selectedCategory, selectedPrices, selectedColors, selectedMaterials]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortId === "menor") list.sort((a, b) => a.price - b.price);
    if (sortId === "maior") list.sort((a, b) => b.price - a.price);
    if (sortId === "novidades") list.sort((a, b) => b.id - a.id);
    if (sortId === "vendidos") list.sort((a, b) => a.id - b.id);
    return list;
  }, [filtered, sortId]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasActiveFilters =
    selectedPrices.length > 0 || selectedColors.length > 0 || selectedMaterials.length > 0;

  const clearFilters = () => {
    setSelectedPrices([]);
    setSelectedColors([]);
    setSelectedMaterials([]);
    setCurrentPage(1);
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      variantId: product.variantId,
    });
    openCart();
    setToast(`${product.name} adicionado à sacola`);
    window.setTimeout(() => setToast(null), 2600);
  };

  return (
    <div className="storefront flex min-h-screen flex-col bg-background-50">
      <HomeIntro />
      <Header />
      <CategoryHero />

      <main className="mx-auto w-full max-w-none px-4 pb-16 pt-10 md:px-6">
        {loadingCatalog && <p role="status">Carregando produtos...</p>}
        {catalogError && <p role="alert">{catalogError} <a href="/contato">Fale conosco</a></p>}
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <SidebarFilters
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={(id) => {
              setSelectedCategory(id);
              setCurrentPage(1);
            }}
            selectedPrices={selectedPrices}
            onPriceToggle={(id) => toggleValue(selectedPrices, id, setSelectedPrices)}
            colorOptions={colorOptions}
            selectedColors={selectedColors}
            onColorToggle={(id) => toggleValue(selectedColors, id, setSelectedColors)}
            materialOptions={materialOptions}
            selectedMaterials={selectedMaterials}
            onMaterialToggle={(id) => toggleValue(selectedMaterials, id, setSelectedMaterials)}
          />

          <ProductListing
            title={title}
            totalCount={filtered.length}
            products={pageItems}
            sortId={sortId}
            onSortChange={setSortId}
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onAddToCart={handleAddToCart}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>
      </main>

      <FeaturesBar />
      <Footer />
      <CartDrawer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3 rounded-lg bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          <span className="flex h-5 w-5 items-center justify-center text-primary-400">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </span>
          {toast}
        </div>
      )}
    </div>
  );
}


